/**
 * DIN Gate Container web component.
 *
 * Light DOM component that validates DIN input and reveals gated content.
 */
class FelixDinGateContainer extends HTMLElement {
	connectedCallback() {
		this.form = this.querySelector('.din-gate-container__form')
		this.input = this.querySelector('.din-gate-container__input')
		this.submitButton = this.querySelector('.din-gate-container__submit')
		this.submitButtonLink = this.querySelector('.din-gate-container__submit .custom-button__link')
		this.error = this.querySelector('.din-gate-container__error')
		this.contentWrap = this.querySelector('.din-gate-container__content-wrap')
		this.overlay = this.querySelector('.din-gate-container__overlay')

		if (!this.form || !this.input || !this.contentWrap) {
			return
		}

		this.allowedDins = this.parseDinCodes(this.dataset.dinCodes)
		this.maxHeightClosed = this.parseMaxHeight(this.dataset.maxHeightClosed)
		this.isUnlocked = false

		this.applyInitialState()
		this.bindEvents()
	}

	disconnectedCallback() {
		if (this.form) {
			this.form.removeEventListener('submit', this.handleSubmit)
		}
		if (this.submitButtonLink) {
			this.submitButtonLink.removeEventListener('click', this.handleSubmitClick)
		}
	}

	parseDinCodes(raw) {
		if (!raw) {
			return []
		}

		try {
			const parsed = JSON.parse(raw)
			if (!Array.isArray(parsed)) {
				return []
			}
			const normalized = parsed
				.map((value) => this.normalizeDin(value))
				.filter((value) => value.length === 8)
			return [...new Set(normalized)]
		} catch (error) {
			return []
		}
	}

	parseMaxHeight(raw) {
		const parsed = parseInt(raw || '', 10)
		return Number.isFinite(parsed) && parsed > 0 ? parsed : 520
	}

	normalizeDin(value) {
		return String(value || '').replace(/\D+/g, '')
	}

	applyInitialState() {
		this.style.setProperty('--din-gate-max-height-closed', `${this.maxHeightClosed}px`)

		if (this.allowedDins.length === 0) {
			this.setError("DIN codes aren't configured for this section yet.")
			if (this.submitButtonLink) {
				this.submitButton.classList.add('custom-button--disabled')
				this.submitButtonLink.setAttribute('aria-disabled', 'true')
				this.submitButtonLink.setAttribute('tabindex', '-1')
				this.submitButtonLink.removeAttribute('href')
			}
			this.input.setAttribute('aria-invalid', 'true')
		}
	}

	bindEvents() {
		this.handleSubmit = this.onSubmit.bind(this)
		this.handleSubmitClick = this.onSubmitClick.bind(this)
		this.form.addEventListener('submit', this.handleSubmit)
		if (this.submitButtonLink) {
			this.submitButtonLink.addEventListener('click', this.handleSubmitClick)
		}
	}

	onSubmitClick(event) {
		event.preventDefault()
		if (this.form && typeof this.form.requestSubmit === 'function') {
			this.form.requestSubmit()
			return
		}
		this.onSubmit(event)
	}

	onSubmit(event) {
		event.preventDefault()

		if (this.isUnlocked || this.allowedDins.length === 0) {
			return
		}

		const normalizedInput = this.normalizeDin(this.input.value)
		if (normalizedInput.length !== 8) {
			this.setError('Please enter a valid 8 digit DIN.')
			this.input.setAttribute('aria-invalid', 'true')
			return
		}

		if (!this.allowedDins.includes(normalizedInput)) {
			this.setError("That DIN code doesn't match. Please check and try again.")
			this.input.setAttribute('aria-invalid', 'true')
			return
		}

		this.clearError()
		this.input.setAttribute('aria-invalid', 'false')
		this.unlock()
	}

	setError(message) {
		if (!this.error) {
			return
		}
		this.error.textContent = message
		this.error.hidden = false
	}

	clearError() {
		if (!this.error) {
			return
		}
		this.error.textContent = ''
		this.error.hidden = true
	}

	unlock() {
		if (this.isUnlocked) {
			return
		}

		this.isUnlocked = true
		this.classList.add('is-unlocked')

		if (this.overlay) {
			this.overlay.setAttribute('aria-hidden', 'true')
		}
	}
}

customElements.define('felix-din-gate-container', FelixDinGateContainer)
